import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import QuestionEditor from './QuestionEditor';
import './QuestionBankManager.css';

const QuestionBankManager = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [filters, setFilters] = useState({
    levelId: '',
    testType: '',
    questionType: ''
  });

  // Load questions from Firestore
  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'testQuestions'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionsData);
    } catch (error) {
      console.error('Error loading questions:', error);
      alert('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setShowEditor(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setShowEditor(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'testQuestions', questionId));
      setQuestions(questions.filter(q => q.id !== questionId));
      alert('Question deleted successfully');
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question');
    }
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      if (editingQuestion) {
        // Update existing question
        await updateDoc(doc(db, 'testQuestions', editingQuestion.id), {
          ...questionData,
          updatedAt: new Date().toISOString()
        });
        alert('Question updated successfully');
      } else {
        // Add new question
        await addDoc(collection(db, 'testQuestions'), {
          ...questionData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        alert('Question added successfully');
      }
      
      setShowEditor(false);
      setEditingQuestion(null);
      loadQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save question');
    }
  };

  // Filter questions
  const filteredQuestions = questions.filter(q => {
    if (filters.levelId && q.levelId !== filters.levelId) return false;
    if (filters.testType && q.testType !== filters.testType) return false;
    if (filters.questionType && q.questionType !== filters.questionType) return false;
    return true;
  });

  // Count questions by test
  const getQuestionCounts = () => {
    const counts = {};
    questions.forEach(q => {
      const key = `${q.levelId}-${q.testType}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return <div className="loading">Loading questions...</div>;
  }

  return (
    <div className="question-bank-manager">
      <div className="manager-header">
        <h2>Question Bank</h2>
        <button onClick={handleAddQuestion} className="add-button">
          + Add Question
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <select 
          value={filters.levelId} 
          onChange={(e) => setFilters({...filters, levelId: e.target.value})}
        >
          <option value="">All Levels</option>
          <option value="level-1">Level 1 - Roots</option>
          <option value="level-2">Level 2 - Branches</option>
          <option value="level-3">Level 3 - Skies</option>
          <option value="level-4">Level 4 - Rivers</option>
        </select>

        <select 
          value={filters.testType} 
          onChange={(e) => setFilters({...filters, testType: e.target.value})}
        >
          <option value="">All Test Types</option>
          <option value="vocab">Vocabulary</option>
          <option value="grammar">Grammar</option>
          <option value="comprehension">Comprehension</option>
        </select>

        <select 
          value={filters.questionType} 
          onChange={(e) => setFilters({...filters, questionType: e.target.value})}
        >
          <option value="">All Question Types</option>
          <option value="mcq">Multiple Choice</option>
          <option value="multiple-response">Multiple Response</option>
          <option value="type-in">Type In</option>
          <option value="order">Order</option>
          <option value="matching">Matching</option>
        </select>
      </div>

      {/* Question counts summary */}
      <div className="question-counts">
        <h3>Question Counts by Test:</h3>
        <div className="counts-grid">
          {Object.entries(getQuestionCounts()).map(([key, count]) => (
            <div key={key} className="count-item">
              <span>{key.replace('-', ' ').toUpperCase()}</span>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* Questions list */}
      <div className="questions-list">
        <p className="results-count">
          Showing {filteredQuestions.length} of {questions.length} questions
        </p>
        
        {filteredQuestions.map((question) => (
          <div key={question.id} className="question-item">
            <div className="question-header">
              <span className="question-meta">
                {question.levelId} | {question.testType} | {question.questionType}
              </span>
              {question.lessonTitle && (
                <span className="lesson-title">📚 {question.lessonTitle}</span>
              )}
            </div>
            <div 
              className="question-content" 
              dangerouslySetInnerHTML={{ __html: question.content?.stem || question.content?.text || 'No content' }}
            />
            <div className="question-actions">
              <button onClick={() => handleEditQuestion(question)} className="edit-btn">
                Edit
              </button>
              <button onClick={() => handleDeleteQuestion(question.id)} className="delete-btn">
                Delete
              </button>
            </div>
          </div>
        ))}

        {filteredQuestions.length === 0 && (
          <div className="no-questions">
            No questions found. Click "Add Question" to create one.
          </div>
        )}
      </div>

      {/* Question Editor Modal */}
      {showEditor && (
        <QuestionEditor
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onCancel={() => {
            setShowEditor(false);
            setEditingQuestion(null);
          }}
        />
      )}
    </div>
  );
};

export default QuestionBankManager;